import { useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { AlertCircle, CheckCircle2, ChevronDown, ChevronUp, History, Pencil, Plus, RefreshCw, Trash2, Search, Upload } from 'lucide-react';
import type { SkillImportEntry, SkillImportResult, SkillRow, UpsertSkillRequest } from '@command-room/shared';
import {
  useAgents,
  useSkills,
  useUpsertSkill,
  useDeleteSkill,
  useImportSkills,
  useReembedSkill,
  useVersions,
  useRestoreVersion,
} from '@/api/queries';
import { TableSkeleton } from '@/components/feedback/Skeletons';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectItem } from '@/components/ui/select';
import { Dialog, DialogContent, DialogClose } from '@/components/ui/dialog';
import { relativeTime } from '@/lib/format';
import { collapseUnchanged, lineDiff, type DiffKind } from '@/lib/lineDiff';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

type SkillFormData = UpsertSkillRequest & { triggerQueriesRaw: string; keywordsRaw: string };

function SkillDialog({
  initial,
  open,
  onOpenChange,
}: {
  initial?: SkillRow;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const upsert = useUpsertSkill();
  const { data: agents } = useAgents();
  const isIndividual = !!initial?.is_individual;

  const { register, handleSubmit, watch, setValue, reset, formState: { errors } } = useForm<SkillFormData>({
    defaultValues: {
      skill_id: initial?.skill_id ?? '',
      skill_name: initial?.skill_name ?? '',
      description: initial?.description ?? '',
      skill_prompt: initial?.skill_prompt ?? '',
      scope: initial?.scope ?? 'global',
      agent_owner: initial?.agent_owner ?? '',
      assigned_agents: initial?.assigned_agents ?? [],
      triggerQueriesRaw: initial?.trigger_queries?.join(', ') ?? '',
      keywordsRaw: initial?.keywords?.join(', ') ?? '',
    },
  });

  const scope = watch('scope');
  const assignedAgents = watch('assigned_agents') ?? [];

  function toggleAgent(name: string) {
    setValue(
      'assigned_agents',
      assignedAgents.includes(name)
        ? assignedAgents.filter((a) => a !== name)
        : [...assignedAgents, name],
    );
  }

  function onClose(open: boolean) {
    if (!open) reset();
    onOpenChange(open);
  }

  async function onSubmit(data: SkillFormData) {
    const payload: UpsertSkillRequest & { id?: number } = {
      id: initial?.id,
      skill_id: data.skill_id,
      skill_name: data.skill_name,
      description: data.description,
      skill_prompt: data.skill_prompt,
      scope: data.scope,
      trigger_queries: data.triggerQueriesRaw
        ? data.triggerQueriesRaw.split(',').map((s) => s.trim()).filter(Boolean)
        : [],
      keywords: data.keywordsRaw
        ? data.keywordsRaw.split(',').map((s) => s.trim()).filter(Boolean)
        : [],
      // Individual skills keep whatever agent_owner they already have (Sandman's domain);
      // regular skills are restricted via assigned_agents instead.
      ...(isIndividual ? { agent_owner: initial?.agent_owner ?? null } : { assigned_agents: data.assigned_agents ?? [] }),
    };
    await upsert.mutateAsync(payload);
    onClose(false);
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        title={initial ? 'Edit skill' : 'New skill'}
        description="Changes to description or trigger queries will trigger a server-side re-embed."
        className="max-w-2xl"
      >
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="skill_id">Skill ID</Label>
              <Input
                id="skill_id"
                {...register('skill_id', { required: true })}
                placeholder="recruiting-101"
                disabled={!!initial}
                className={errors.skill_id ? 'border-destructive' : ''}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="skill_name">Display name</Label>
              <Input
                id="skill_name"
                {...register('skill_name', { required: true })}
                placeholder="Recruiting Fundamentals"
                className={errors.skill_name ? 'border-destructive' : ''}
              />
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>Scope</Label>
            <Select
              value={scope}
              onValueChange={(v) => setValue('scope', v as 'global' | 'dynamic')}
            >
              <SelectItem value="global">Global (loaded every request)</SelectItem>
              <SelectItem value="dynamic">Dynamic (RAG — Think Harder only)</SelectItem>
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>Assigned agents</Label>
            {isIndividual ? (
              <p className="text-sm text-muted-foreground">
                Sandman-curated for <span className="font-medium text-foreground">{initial?.agent_owner}</span> — not
                manually assignable.
              </p>
            ) : (
              <>
                <div className="flex flex-wrap gap-1.5">
                  {(agents ?? [])
                    .filter((a) => a.type === 'AI')
                    .map((a) => (
                      <button
                        key={a.name}
                        type="button"
                        onClick={() => toggleAgent(a.name)}
                        className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${
                          assignedAgents.includes(a.name)
                            ? 'border-primary bg-primary text-primary-foreground'
                            : 'border-border bg-transparent text-foreground hover:bg-muted/40'
                        }`}
                      >
                        {a.name}
                      </button>
                    ))}
                </div>
                <p className="text-xs text-muted-foreground">
                  {assignedAgents.length === 0
                    ? 'None selected — available to all agents.'
                    : `Restricted to ${assignedAgents.length} agent${assignedAgents.length === 1 ? '' : 's'}.`}
                </p>
              </>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              {...register('description', { required: true })}
              rows={2}
              placeholder="Short description used for embedding and similarity search."
              className={errors.description ? 'border-destructive' : ''}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="trigger_queries">Trigger queries (comma-separated)</Label>
            <Input
              id="trigger_queries"
              {...register('triggerQueriesRaw')}
              placeholder="find candidates, source talent, screen CV"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="keywords">Keywords (comma-separated)</Label>
            <Input
              id="keywords"
              {...register('keywordsRaw')}
              placeholder="recruiting, sourcing, interview"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="skill_prompt">
              Skill prompt{' '}
              <span className="text-muted-foreground font-normal text-xs">(injected into system prompt)</span>
            </Label>
            <Textarea
              id="skill_prompt"
              {...register('skill_prompt', { required: true })}
              rows={10}
              className={`font-mono text-xs ${errors.skill_prompt ? 'border-destructive' : ''}`}
              placeholder="Write the skill content here. This is injected verbatim into the agent's system prompt when matched."
            />
          </div>

          {upsert.error && (
            <p className="text-sm text-destructive flex items-center gap-1">
              <AlertCircle className="h-4 w-4" />
              {(upsert.error as Error).message}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <DialogClose asChild>
              <Button type="button" variant="outline">Cancel</Button>
            </DialogClose>
            <Button type="submit" disabled={upsert.isPending}>
              {upsert.isPending ? 'Saving…' : 'Save skill'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── Skill import dialog ──────────────────────────────────────────────────────

type ImportPreviewEntry = SkillImportEntry & { _status: 'new' | 'update' | 'unknown' };

function parseImportJson(raw: string): { entries: SkillImportEntry[]; error: string | null } {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { entries: [], error: 'Invalid JSON' };
  }

  // Accept either an array of skill entries or the index.json format with top-level array
  const arr: unknown[] = Array.isArray(parsed) ? parsed : [];
  if (arr.length === 0) {
    return { entries: [], error: 'Expected a JSON array of skill objects' };
  }

  const entries: SkillImportEntry[] = [];
  for (const item of arr) {
    if (typeof item !== 'object' || !item) continue;
    const s = item as Record<string, unknown>;
    if (s.status === 'blocked') continue;
    const skillName = s.skill_name ?? s.name;
    const skillPrompt = s.skill_prompt ?? s.content;
    if (!s.skill_id || !skillName || !s.description || !skillPrompt) continue;
    entries.push({
      skill_id: String(s.skill_id),
      skill_name: String(skillName),
      description: String(s.description),
      skill_prompt: String(skillPrompt),
      scope: (s.scope === 'global' || s.scope === 'dynamic') ? s.scope : 'dynamic',
      agent_owner: typeof s.agent_owner === 'string' ? s.agent_owner : null,
      assigned_agents: Array.isArray(s.assigned_agents) ? (s.assigned_agents as string[]) : undefined,
      trigger_queries: Array.isArray(s.trigger_queries) ? (s.trigger_queries as string[]) : [],
      keywords: Array.isArray(s.keywords) ? (s.keywords as string[]) : [],
    });
  }

  if (entries.length === 0) {
    return { entries: [], error: 'No valid skill entries found (need skill_id, skill_name, description, skill_prompt)' };
  }

  return { entries, error: null };
}

function ImportDialog({
  existingSkillIds,
  open,
  onOpenChange,
}: {
  existingSkillIds: Set<string>;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const importSkills = useImportSkills();
  const fileRef = useRef<HTMLInputElement>(null);
  const [raw, setRaw] = useState('');
  const [parseError, setParseError] = useState<string | null>(null);
  const [preview, setPreview] = useState<ImportPreviewEntry[]>([]);
  const [result, setResult] = useState<SkillImportResult | null>(null);

  function reset() {
    setRaw('');
    setParseError(null);
    setPreview([]);
    setResult(null);
  }

  function onClose(v: boolean) {
    if (!v) reset();
    onOpenChange(v);
  }

  function processRaw(text: string) {
    setRaw(text);
    setResult(null);
    if (!text.trim()) {
      setPreview([]);
      setParseError(null);
      return;
    }
    const { entries, error } = parseImportJson(text);
    setParseError(error);
    setPreview(
      entries.map((e) => ({
        ...e,
        _status: existingSkillIds.has(e.skill_id) ? 'update' : 'new',
      })),
    );
  }

  function onFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => processRaw(ev.target?.result as string);
    reader.readAsText(file);
  }

  async function onImport() {
    if (preview.length === 0) return;
    const r = await importSkills.mutateAsync(preview);
    setResult(r);
  }

  const newCount = preview.filter((e) => e._status === 'new').length;
  const updateCount = preview.filter((e) => e._status === 'update').length;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        title="Import skills"
        description="Paste JSON or upload a file. Accepts an array of skill objects or a skills/index.json file."
        className="max-w-3xl"
      >
        <div className="flex flex-col gap-4">
          {!result && (
            <>
              <div className="flex items-center gap-3">
                <input
                  ref={fileRef}
                  type="file"
                  accept=".json"
                  className="hidden"
                  onChange={onFileChange}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileRef.current?.click()}
                >
                  <Upload className="h-4 w-4" />
                  Upload JSON file
                </Button>
                <span className="text-xs text-muted-foreground">or paste below</span>
              </div>

              <textarea
                className="min-h-[120px] w-full rounded-md border border-border bg-muted/30 p-3 font-mono text-xs resize-y focus:outline-none focus:ring-1 focus:ring-ring"
                placeholder={'[\n  { "skill_id": "…", "skill_name": "…", … }\n]'}
                value={raw}
                onChange={(e) => processRaw(e.target.value)}
              />

              {parseError && (
                <p className="text-sm text-destructive flex items-center gap-1">
                  <AlertCircle className="h-4 w-4" /> {parseError}
                </p>
              )}

              {preview.length > 0 && (
                <div className="flex flex-col gap-2">
                  <p className="text-xs text-muted-foreground">
                    {preview.length} skill{preview.length !== 1 ? 's' : ''} —{' '}
                    <span className="text-foreground">{newCount} new</span>{', '}
                    <span className="text-foreground">{updateCount} update</span>
                  </p>
                  <div className="max-h-48 overflow-auto rounded-lg border border-border text-xs">
                    <table className="w-full">
                      <thead className="bg-muted/40 text-left sticky top-0">
                        <tr>
                          <th className="px-3 py-2">Skill ID</th>
                          <th className="px-3 py-2">Name</th>
                          <th className="px-3 py-2">Scope</th>
                          <th className="px-3 py-2">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {preview.map((e) => (
                          <tr key={e.skill_id} className="hover:bg-muted/20">
                            <td className="px-3 py-1.5 font-mono">{e.skill_id}</td>
                            <td className="px-3 py-1.5">{e.skill_name}</td>
                            <td className="px-3 py-1.5">{e.scope}</td>
                            <td className="px-3 py-1.5">
                              <span className={e._status === 'new' ? 'text-success' : 'text-warning-foreground'}>
                                {e._status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {importSkills.error && (
                <p className="text-sm text-destructive flex items-center gap-1">
                  <AlertCircle className="h-4 w-4" />
                  {(importSkills.error as Error).message}
                </p>
              )}

              <div className="flex justify-end gap-2 pt-1">
                <DialogClose asChild>
                  <Button variant="outline" type="button">Cancel</Button>
                </DialogClose>
                <Button
                  onClick={onImport}
                  disabled={preview.length === 0 || importSkills.isPending}
                >
                  {importSkills.isPending
                    ? 'Importing…'
                    : `Import ${preview.length} skill${preview.length !== 1 ? 's' : ''}`}
                </Button>
              </div>
            </>
          )}

          {result && (
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle2 className="h-4 w-4 text-success" />
                Import complete
              </div>
              <dl className="grid grid-cols-3 gap-4 text-center">
                <div className="rounded-lg border border-border p-3">
                  <dt className="text-xs text-muted-foreground">Imported</dt>
                  <dd className="text-2xl font-semibold text-success">{result.imported}</dd>
                </div>
                <div className="rounded-lg border border-border p-3">
                  <dt className="text-xs text-muted-foreground">Updated</dt>
                  <dd className="text-2xl font-semibold">{result.updated}</dd>
                </div>
                <div className="rounded-lg border border-border p-3">
                  <dt className="text-xs text-muted-foreground">Unchanged</dt>
                  <dd className="text-2xl font-semibold text-muted-foreground">{result.unchanged}</dd>
                </div>
              </dl>
              {result.errors.length > 0 && (
                <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3">
                  <p className="text-xs font-medium text-destructive mb-2">{result.errors.length} error{result.errors.length !== 1 ? 's' : ''}:</p>
                  <ul className="space-y-1 text-xs text-destructive/80">
                    {result.errors.map((e) => (
                      <li key={e.skill_id}><span className="font-mono">{e.skill_id}</span>: {e.message}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="flex justify-end">
                <Button onClick={() => onClose(false)}>Done</Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

const DIFF_ROW_STYLE: Record<DiffKind, string> = {
  same: 'text-muted-foreground',
  added: 'bg-success/10 text-success',
  removed: 'bg-destructive/10 text-destructive',
};
const DIFF_SIGN: Record<DiffKind, string> = { same: ' ', added: '+', removed: '-' };

function VersionDetail({ prompt, current }: { prompt: string; current: string }) {
  const rows = useMemo(() => collapseUnchanged(lineDiff(prompt, current)), [prompt, current]);
  const changed = rows.some((r) => r.kind === 'added' || r.kind === 'removed');
  const lineCount = prompt === '' ? 0 : prompt.split('\n').length;

  return (
    <Tabs defaultValue="full" className="flex flex-col gap-2">
      <TabsList className="self-start">
        <TabsTrigger value="full">Full text</TabsTrigger>
        <TabsTrigger value="diff">Changes vs current</TabsTrigger>
      </TabsList>
      <TabsContent value="full" className="flex flex-col gap-1">
        <pre className="max-h-[50vh] overflow-y-auto rounded-md bg-muted/40 p-3 font-mono text-xs whitespace-pre-wrap break-words">
          {prompt}
        </pre>
        <span className="text-xs text-muted-foreground">
          {lineCount} lines · {prompt.length} chars
        </span>
      </TabsContent>
      <TabsContent value="diff" className="flex flex-col gap-1">
        {!changed ? (
          <p className="text-sm text-muted-foreground">Identical to the current prompt — restoring it changes nothing.</p>
        ) : (
          <>
            <p className="text-xs text-muted-foreground">
              <span className="text-destructive">−</span> only in this version (a restore brings it back) ·{' '}
              <span className="text-success">+</span> only in the current prompt (a restore removes it)
            </p>
            <div className="max-h-[50vh] overflow-y-auto rounded-md bg-muted/40 py-2 font-mono text-xs">
              {rows.map((r, i) =>
                r.kind === 'skip' ? (
                  <div key={i} className="px-3 py-0.5 italic text-muted-foreground/70">
                    … {r.count} unchanged lines
                  </div>
                ) : (
                  <div key={i} className={`flex gap-2 px-3 whitespace-pre-wrap break-words ${DIFF_ROW_STYLE[r.kind]}`}>
                    <span className="select-none">{DIFF_SIGN[r.kind]}</span>
                    <span className="min-w-0 flex-1">{r.text || ' '}</span>
                  </div>
                ),
              )}
            </div>
          </>
        )}
      </TabsContent>
    </Tabs>
  );
}

function VersionHistoryDialog({
  skill,
  open,
  onOpenChange,
}: {
  skill: SkillRow | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const { data: versions, isLoading } = useVersions('skill', skill?.skill_id);
  const restore = useRestoreVersion();
  const [confirmId, setConfirmId] = useState<number | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) setExpandedId(null); onOpenChange(v); }}>
      <DialogContent
        className="max-w-4xl"
        title={`History — ${skill?.skill_name ?? ''}`}
        description="Every prior version this skill's prompt has had. Restoring writes a fresh version of the current content first, so this is itself reversible."
      >
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (versions ?? []).length === 0 ? (
          <p className="text-sm text-muted-foreground">No prior versions — this skill hasn't changed since it was created.</p>
        ) : (
          <div className="flex flex-col gap-2 max-h-[70vh] overflow-y-auto">
            {(versions ?? []).map((v) => {
              const expanded = expandedId === v.id;
              const prompt = String(v.snapshot.skill_prompt ?? '');
              return (
              <div key={v.id} className="rounded-md border border-border p-3 flex flex-col gap-2">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Badge variant={v.change_source === 'sandman' ? 'secondary' : 'outline'}>
                      {v.change_source}
                    </Badge>
                    <span title={new Date(v.created_at).toLocaleString()}>{relativeTime(v.created_at)}</span>
                  </div>
                  <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-expanded={expanded}
                    onClick={() => setExpandedId(expanded ? null : v.id)}
                  >
                    {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    {expanded ? 'Hide' : 'Show full'}
                  </Button>
                  {confirmId === v.id ? (
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        disabled={restore.isPending}
                        onClick={() => restore.mutate(v.id, { onSuccess: () => setConfirmId(null) })}
                      >
                        {restore.isPending ? 'Restoring…' : 'Confirm restore'}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setConfirmId(null)}>Cancel</Button>
                    </div>
                  ) : (
                    <Button variant="outline" size="sm" onClick={() => setConfirmId(v.id)}>Restore</Button>
                  )}
                  </div>
                </div>
                {expanded ? (
                  <VersionDetail prompt={prompt} current={skill?.skill_prompt ?? ''} />
                ) : (
                  <button
                    type="button"
                    className="text-left text-xs text-muted-foreground line-clamp-3 whitespace-pre-wrap hover:text-foreground"
                    onClick={() => setExpandedId(v.id)}
                  >
                    {prompt}
                  </button>
                )}
              </div>
              );
            })}
          </div>
        )}
        <div className="flex justify-end pt-2">
          <DialogClose asChild>
            <Button variant="outline">Close</Button>
          </DialogClose>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function SkillsPage() {
  const { data: skills, isLoading } = useSkills();
  const deleteSkill = useDeleteSkill();
  const reembed = useReembedSkill();

  const [search, setSearch] = useState('');
  const [scopeFilter, setScopeFilter] = useState<'all' | 'global' | 'dynamic'>('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editing, setEditing] = useState<SkillRow | undefined>(undefined);
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);
  const [historySkill, setHistorySkill] = useState<SkillRow | null>(null);

  const existingSkillIds = new Set((skills ?? []).map((s) => s.skill_id));

  const filtered = (skills ?? []).filter((s) => {
    if (scopeFilter !== 'all' && s.scope !== scopeFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        s.skill_name.toLowerCase().includes(q) ||
        s.skill_id.toLowerCase().includes(q) ||
        s.description.toLowerCase().includes(q)
      );
    }
    return true;
  });

  const dirtyCount = (skills ?? []).filter((s) => s.embedding_dirty).length;

  function openCreate() {
    setEditing(undefined);
    setDialogOpen(true);
  }

  function openEdit(skill: SkillRow) {
    setEditing(skill);
    setDialogOpen(true);
  }

  return (
    <div className="mx-auto flex max-w-7xl flex-col gap-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Skills</h1>
          <p className="text-sm text-muted-foreground">
            {skills?.length ?? 0} skill{skills?.length === 1 ? '' : 's'}
            {dirtyCount > 0 && (
              <span className="ml-2 text-warning-foreground">
                · {dirtyCount} embedding{dirtyCount === 1 ? '' : 's'} need re-embed
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setImportOpen(true)}>
            <Upload className="h-4 w-4" />
            Import JSON
          </Button>
          <Button onClick={openCreate}>
            <Plus className="h-4 w-4" />
            New skill
          </Button>
        </div>
      </header>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Search skills…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Select value={scopeFilter} onValueChange={(v) => setScopeFilter(v as typeof scopeFilter)} className="w-40">
          <SelectItem value="all">All scopes</SelectItem>
          <SelectItem value="global">Global</SelectItem>
          <SelectItem value="dynamic">Dynamic</SelectItem>
        </Select>
      </div>

      {isLoading ? (
        <TableSkeleton widths={['w-44', 'w-16', 'w-24', 'w-56', 'w-16', 'w-20']} rows={8} />
      ) : (
        <div className="rounded-lg border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/30 text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Skill</th>
                <th className="px-4 py-3">Scope</th>
                <th className="px-4 py-3">Assigned to</th>
                <th className="px-4 py-3 hidden lg:table-cell">Description</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                    No skills match your filter.
                  </td>
                </tr>
              ) : (
                filtered.map((skill) => (
                  <tr key={skill.id} className="hover:bg-muted/20">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <p className="font-medium">{skill.skill_name}</p>
                        {skill.is_individual && <Badge variant="secondary">individual</Badge>}
                        {skill.status === 'retired' && <Badge variant="muted">retired</Badge>}
                      </div>
                      <p className="text-xs text-muted-foreground font-mono">{skill.skill_id}</p>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={skill.scope === 'global' ? 'default' : 'secondary'}>
                        {skill.scope}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {skill.is_individual ? (
                        skill.agent_owner ?? '—'
                      ) : skill.assigned_agents.length === 0 ? (
                        <span className="text-xs italic">all agents</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {skill.assigned_agents.map((a) => (
                            <Badge key={a} variant="outline" className="text-xs">{a}</Badge>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell text-muted-foreground max-w-xs">
                      <span className="line-clamp-2">{skill.description}</span>
                    </td>
                    <td className="px-4 py-3">
                      {skill.embedding_dirty ? (
                        <Badge variant="warning">dirty</Badge>
                      ) : (
                        <Badge variant="success">ok</Badge>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {skill.embedding_dirty && (
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Re-embed"
                            disabled={reembed.isPending}
                            onClick={() => reembed.mutate(skill.id)}
                          >
                            <RefreshCw className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          title="History"
                          onClick={() => setHistorySkill(skill)}
                        >
                          <History className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Edit"
                          onClick={() => openEdit(skill)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        {confirmDelete === skill.id ? (
                          <div className="flex items-center gap-1">
                            <Button
                              variant="destructive"
                              size="sm"
                              disabled={deleteSkill.isPending}
                              onClick={() => {
                                deleteSkill.mutate(skill.id, {
                                  onSuccess: () => setConfirmDelete(null),
                                });
                              }}
                            >
                              Delete
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setConfirmDelete(null)}
                            >
                              Cancel
                            </Button>
                          </div>
                        ) : (
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Delete"
                            onClick={() => setConfirmDelete(skill.id)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <SkillDialog
        key={editing?.id ?? 'new'}
        initial={editing}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
      />

      <ImportDialog
        existingSkillIds={existingSkillIds}
        open={importOpen}
        onOpenChange={setImportOpen}
      />

      <VersionHistoryDialog
        skill={historySkill}
        open={!!historySkill}
        onOpenChange={(open) => { if (!open) setHistorySkill(null); }}
      />
    </div>
  );
}
